import React, {useEffect, useState} from 'react';
import {Modal, SelectInput, Text, Icon, IconType, Button} from '@/ui-kits';
import {Doctor, SelectInputitem} from '@/interface';
import {appendPlural, cn} from '@/utils/common_functions';
import {ArrowRightIcon} from 'lucide-react';
import {useDispatch, useSelector} from 'react-redux';
import {assignHospitalSpecialistRequest, getHospitalSpecialistsListRequest, resetPatientDashboardMessage, selectHospitalSpecialistRequest} from '@/services/redux/slices';
import {
  hospitalSpecialistsListData,
  hospitalSpecialistsListLoadingStatus,
  patientDashboardFailureState,
  patientDashboardSuccessState,
} from '@/services/redux/selectors';
import {ErrorCodes, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {Routes} from '@/navigation/Routes';
import {useNavigate} from 'react-router-dom';
import {useToast} from '@/hooks';
import {ArrowLeft} from 'lucide-react';
import {SpecialistCard} from '@/components';

// ================
// Interface Definitions
// ================
interface HospitalBookingModalProps {
  open: boolean;
  onClose: () => void;
  hospitalName: string;
  hospitalId: number;
  specializations: SelectInputitem[];
  /** Initial state when restoring modal after navigation (e.g. back from specialist profile) */
  initialSelectedSpecialization?: number | null;
  initialIsStep2?: boolean;
  /** Called when modal state changes so parent can persist for back-navigation restore */
  onStateChange?: (data: {selectedSpecialization: number | null; isStep2: boolean}) => void;
}

// ================
// Component
// ================
export const HospitalBookingModal: React.FC<HospitalBookingModalProps> = ({
  open,
  onClose,
  hospitalName,
  hospitalId,
  specializations,
  initialSelectedSpecialization = null,
  initialIsStep2 = false,
  onStateChange,
}) => {
  // ================
  // hooks
  // ================
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {showToast} = useToast();
  // ================
  // selectors
  // ================
  const isSpecialistsListLoading = useSelector(hospitalSpecialistsListLoadingStatus);
  const specialistsList = useSelector(hospitalSpecialistsListData);
  const success = useSelector(patientDashboardSuccessState) as SuccessCodes;
  const failure = useSelector(patientDashboardFailureState) as ErrorCodes;

  // ================
  // states
  // ================
  const [selectedSpecialization, setSelectedSpecialization] = useState<number | null>(initialSelectedSpecialization ?? null);
  const [isSelectedMethod, setIsSelectedMethod] = useState<boolean | undefined>(initialIsStep2 ? true : undefined);
  const [selectedSpecialist, setSelectedSpecialist] = useState<Doctor | null>(null);

  // Sync with initial props when modal opens (e.g. restoring from back navigation)
  useEffect(() => {
    if (open) {
      setSelectedSpecialization(initialSelectedSpecialization ?? null);
      setIsSelectedMethod(initialIsStep2 ? true : undefined);
      setSelectedSpecialist(null);
      // Fetch specialists when restoring to step 2
      if (initialIsStep2 && initialSelectedSpecialization) {
        dispatch(
          getHospitalSpecialistsListRequest({
            hospital_id: hospitalId,
            specialization: initialSelectedSpecialization,
          }),
        );
      }
    }
  }, [open, initialSelectedSpecialization, initialIsStep2, hospitalId, dispatch]);

  // ================
  // functions
  // ================
  const handleAssignSpecialist = () => {
    dispatch(
      assignHospitalSpecialistRequest({
        id: hospitalId,
        ...(selectedSpecialization && {specialization: selectedSpecialization}),
      }),
    );
  };

  const handleSelectSpecialist = () => {
    if (!selectedSpecialization) return;
    dispatch(
      getHospitalSpecialistsListRequest({
        hospital_id: hospitalId,
        specialization: selectedSpecialization,
      }),
    );
    setIsSelectedMethod(true);
    onStateChange?.({
      selectedSpecialization,
      isStep2: true,
    });
  };
  // Convert specializations to SelectInputitem format
  const specializationOptions: SelectInputitem[] = specializations.map(spec => ({
    id: spec.id,
    label: spec.label,
  }));

  const handleChangeSpecialization = (item: SelectInputitem) => {
    setSelectedSpecialization(item.id);
    dispatch(
      getHospitalSpecialistsListRequest({
        hospital_id: hospitalId,
        specialization: item.id,
      }),
    );
    onStateChange?.({
      selectedSpecialization: item.id,
      isStep2: isSelectedMethod ?? false,
    });
  };

  const handleBookNow = () => {
    if (!selectedSpecialist?.id) return;
    dispatch(
      selectHospitalSpecialistRequest({
        id: selectedSpecialist.id,
      }),
    );
  };

  // =====================
  // effects
  // =====================
  useEffect(() => {
    if (success) {
      if (success === 'S-10097') {
        showToast(getSuccessMessage(success), 'success');
        navigate(Routes.DASHBOARD);
        onClose();
      }
      //
      if (success === 'S-10096') {
        setIsSelectedMethod(true);
      }
    }
    return () => {
      dispatch(resetPatientDashboardMessage());
    };
  }, [success, failure, navigate]);

  // ================
  // Render
  // ================
  return (
    <Modal open={open} onClose={onClose} className="relative bg-white md:max-w-[600px] 3xl:max-w-[700px] w-full p-6 sm:p-8 rounded-2xl gap-4 box-border
">
      {isSelectedMethod ? (
        <>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setIsSelectedMethod(false);
                onStateChange?.({selectedSpecialization, isStep2: false});
              }}>
              <ArrowLeft className="size-6 text-ui_blue" />
            </button>
            <Text variant="titleLarge" className="text-ui_blue grow !font-FigtreeBold text-xl sm:text-2xl pr-8">
              Select a Specialist
            </Text>

            <button
              onClick={onClose}
              className="flex-shrink-0 size-8 -translate-y-4 translate-x-2 rounded-full flex items-center justify-center transition-colors">
              <Icon type="close" className="size-6 text-ui_blue" />
            </button>
          </div>
          <Text className="text-ui_blue !font-FigtreeSemiBold ml-8">{hospitalName}</Text>
          <SelectInput
            value={selectedSpecialization}
            options={specializationOptions}
            onChange={handleChangeSpecialization}
            placeholder="Select a specialization"
            wrapperClassName="!h-[48px] border"
            className="!w-full my-4"
            textClassName="text-ui_input_border_dark"
          />

          {specialistsList.length > 0 && (
            <div className="flex my-6 gap-1 items-center">
              <Icon type="select_specialist_model" className="size-6 text-ui_blue" />
              <Text variant="small" className="text-ui_blue pr-8">
                {specialistsList.length} {appendPlural('Specialist', specialistsList.length)} Available
              </Text>
            </div>
          )}

          {specialistsList.length > 0 ? (
            <div className="flex overflow-x-auto py-5 gap-4">
              {specialistsList.map(specialist => (
                <>
                  <SpecialistCard
                    key={specialist.id}
                    doctor={specialist}
                    className="!max-w-[280px]"
                    isSelected={selectedSpecialist?.id === specialist.id}
                    onSelect={() => setSelectedSpecialist(specialist)}
                    nameSize="text-xl"
                  />
                </>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4 h-32 justify-center items-center">
              <Text variant="body" className="text-ui_blue !font-FigtreeSemiBold">
                No specialists available for this specialization.
              </Text>
            </div>
          )}

          <Button
            title="Book Now"
            onClick={handleBookNow}
            disabled={!selectedSpecialization || !selectedSpecialist}
            variant="primary"
            className="w-full mt-4 max-w-[140px] mx-auto self-center"
          />
        </>
      ) : (
        <>
          <div className="flex items-start justify-between mb-4">
            <Text variant="title" className="text-ui_blue !font-FigtreeBold text-xl sm:text-2xl pr-8">
              Select Your Booking Method – {hospitalName}
            </Text>
            <button onClick={onClose} className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors">
              <Icon type="close" className="size-6 text-ui_blue" />
            </button>
          </div>

          <div className="mb-8 flex flex-col items-center">
            <Text variant="body" className="text-ui_blue mb-6 !font-FigtreeSemiBold">
              Please select a specialization to proceed with booking.
            </Text>

            <SelectInput
              value={selectedSpecialization}
              options={specializationOptions}
              onChange={item => {
                setSelectedSpecialization(item.id);
                onStateChange?.({
                  selectedSpecialization: item.id,
                  isStep2: false,
                });
              }}
              placeholder="Select a specialization"
              wrapperClassName="!h-[48px] border"
              className="!w-full max-w-[400px]"
              textClassName="text-ui_input_border_dark"
            />
          </div>

          <div className="flex flex-col gap-4">
            {/* Assign Specialist Option */}
            <BookingOption
              icon="assign_specialist"
              title="Assign Specialist for Me"
              description="Let the hospital assign the best available specialist based on your needs."
              onClick={handleAssignSpecialist}
              disabled={!selectedSpecialization}
            />
            <BookingOption
              icon="select_specialist"
              title="Select Specialist Yourself"
              description="Browse available specialists, filter by specialization and choose your preferred doctor."
              onClick={handleSelectSpecialist}
              disabled={!selectedSpecialization}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

interface BookingOptionProps {
  icon: IconType;
  title: string;
  description: string;
  onClick: () => void;
  disabled: boolean;
}
const BookingOption: React.FC<BookingOptionProps> = props => {
  const {icon, title, description, onClick, disabled} = props;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'relative flex items-center gap-4 p-4 rounded-xl border transition-all hover:shadow-md',
        'bg-gray-50 border-gray-200',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'hover:border-ui_blue cursor-pointer',
      )}>
      {/* Icon */}
      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#CBEBFB] flex items-center justify-center">
        <Icon type={icon} className="size-6 text-ui_blue" />
      </div>

      {/* Content */}
      <div className="flex-1 text-left">
        <Text variant="title" className="font-FigtreeBold text-ui_blue mb-1">
          {title}
        </Text>
        <Text variant="small" className="text-ui_input_border_dark">
          {description}
        </Text>
      </div>

      {/* Arrow Icon */}
      <ArrowRightIcon className="size-5 text-ui_disabled" />
    </button>
  );
};
